/** The delivery rows: export jobs, notification deliveries, quiet hours; mirrors `ssi.api.views_delivery`. */
import {
  ContractViolation,
  bool,
  exact,
  field,
  instant,
  nat,
  nullable,
  oneOf,
  str,
  type Reader,
} from "./decode.ts";
import { MODES, type Mode } from "./envelope.ts";

export const JOBS_PATH = "/api/v1/jobs";
export const QUIET_HOURS_PATH = "/api/v1/notifications/quiet-hours";
export const MINUTES_PER_DAY = 24 * 60;
export const EXPORTS_VIEW = "exports";
export const NOTIFICATIONS_VIEW = "notifications";

export const JOB_STATES = [
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCEL_REQUESTED",
  "CANCELLED",
  "EXPIRED",
] as const;
export const JOB_KINDS = ["REPORT", "PERIODIC_REVIEW", "EXPIRING_EXPORT"] as const;
export const JOB_FAILURES = [
  "GRANT_REVOKED",
  "LICENCE_HELD",
  "SOURCE_ERROR",
  "ROW_TOO_LARGE",
  "ROW_LIMIT",
] as const;
export const FIDELITIES = [
  "BAR_SCREEN",
  "QUOTE_REPLAY",
  "EVENT_REPLAY",
  "OBSERVED_EXECUTION",
] as const;
export const DELIVERY_STATES = [
  "INTENDED",
  "SENT",
  "ACCEPTED",
  "DELIVERED",
  "FAILED",
  "EXPIRED",
  "SUPERSEDED",
] as const;
export const DELIVERY_REASONS = [
  "NEWER_VERSION",
  "DIGESTED",
  "EXPIRED",
  "SEND_FAILED",
  "SEND_TIMEOUT",
  "PROVIDER_FAILED",
  "NOT_ENTITLED",
  "LIMITED",
] as const;
export const CALLBACK_KINDS = [
  "DUPLICATE",
  "FORGED",
  "UNVERIFIABLE",
  "UNKNOWN_MESSAGE",
  "STALE",
  "MALFORMED",
] as const;
export const CHANNELS = ["PUSH", "EMAIL", "SMS"] as const;
export const SEVERITIES = ["NORMAL", "SEV1", "MANDATORY"] as const;
export const NOT_SILENCEABLE = ["SEV1", "MANDATORY"] as const;
export const DIRECTIONS = ["LONG", "SHORT"] as const;

export type JobState = (typeof JOB_STATES)[number];
export type JobKind = (typeof JOB_KINDS)[number];
export type JobFailure = (typeof JOB_FAILURES)[number];
export type Fidelity = (typeof FIDELITIES)[number];
export type DeliveryState = (typeof DELIVERY_STATES)[number];
export type DeliveryReason = (typeof DELIVERY_REASONS)[number];
export type CallbackKind = (typeof CALLBACK_KINDS)[number];

const HEX64 = /^[0-9a-f]{64}$/;
const MANIFEST_STATES: readonly JobState[] = ["SUCCEEDED", "EXPIRED"];
const REASON_STATES: readonly DeliveryState[] = ["FAILED", "EXPIRED", "SUPERSEDED"];

export interface ExportJobRow {
  readonly kind: "export_job";
  readonly jobId: string;
  readonly jobKind: JobKind;
  readonly state: JobState;
  readonly workspaceId: string;
  readonly accountId: string | null;
  readonly sharedWorkspace: string | null;
  readonly mode: Mode;
  readonly fidelity: Fidelity;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly finishedAt: string | null;
  readonly failure: JobFailure | null;
  /** The sealed artifact's facts; present together or not at all, and only once a job succeeded. */
  readonly artifact: Artifact | null;
}

export interface Artifact {
  readonly chunks: number;
  readonly totalRows: number;
  readonly totalBytes: number;
  readonly manifestDigest: string;
}

const EXPORT_KEYS = [
  "kind",
  "job_id",
  "job_kind",
  "state",
  "workspace_id",
  "account_id",
  "shared_workspace",
  "mode",
  "fidelity",
  "created_at",
  "expires_at",
  "finished_at",
  "failure",
  "chunks",
  "total_rows",
  "total_bytes",
  "manifest_digest",
] as const;

function digest(value: unknown, path: string): string {
  const text = str(value, path);
  if (!HEX64.test(text)) {
    throw new ContractViolation(path, "a SHA-256 digest");
  }
  return text;
}

/** Decode an export job row; the cross-field rules of the Python contract are enforced too. */
export function decodeExportJobRow(value: unknown, path = "export"): ExportJobRow {
  const found = exact(value, path, EXPORT_KEYS);
  if (found.kind !== "export_job") {
    throw new ContractViolation(`${path}.kind`, "export_job");
  }
  const chunks = field(found, path, "chunks", nullable(nat));
  const totalRows = field(found, path, "total_rows", nullable(nat));
  const totalBytes = field(found, path, "total_bytes", nullable(nat));
  const manifestDigest = field(found, path, "manifest_digest", nullable(digest));
  const facts = [chunks, totalRows, totalBytes, manifestDigest];
  let artifact: Artifact | null = null;
  if (chunks !== null && totalRows !== null && totalBytes !== null && manifestDigest !== null) {
    artifact = { chunks, totalRows, totalBytes, manifestDigest };
  } else if (!facts.every((fact) => fact === null)) {
    throw new ContractViolation(path, "the artifact facts present together or not at all");
  }
  const row: ExportJobRow = {
    kind: "export_job",
    jobId: field(found, path, "job_id", str),
    jobKind: field(found, path, "job_kind", oneOf(JOB_KINDS)),
    state: field(found, path, "state", oneOf(JOB_STATES)),
    workspaceId: field(found, path, "workspace_id", str),
    accountId: field(found, path, "account_id", nullable(str)),
    sharedWorkspace: field(found, path, "shared_workspace", nullable(str)),
    mode: field(found, path, "mode", oneOf(MODES)),
    fidelity: field(found, path, "fidelity", oneOf(FIDELITIES)),
    createdAt: field(found, path, "created_at", instant),
    expiresAt: field(found, path, "expires_at", instant),
    finishedAt: field(found, path, "finished_at", nullable(instant)),
    failure: field(found, path, "failure", nullable(oneOf(JOB_FAILURES))),
    artifact,
  };
  if (row.expiresAt <= row.createdAt) {
    throw new ContractViolation(path, "a job that expires after it is created");
  }
  if ((row.failure !== null) !== (row.state === "FAILED")) {
    throw new ContractViolation(path, "a failure reason exactly for a FAILED job");
  }
  if (row.artifact !== null && !MANIFEST_STATES.includes(row.state)) {
    throw new ContractViolation(path, "an artifact only for a job that succeeded");
  }
  return row;
}

export interface DeliveryRow {
  readonly kind: "delivery";
  readonly intentId: string;
  readonly recommendationId: string;
  readonly version: number;
  readonly channel: (typeof CHANNELS)[number];
  readonly severity: (typeof SEVERITIES)[number];
  readonly state: DeliveryState;
  readonly reason: DeliveryReason | null;
  readonly direction: (typeof DIRECTIONS)[number] | null;
  readonly mode: Mode;
  readonly expiry: string;
  readonly createdAt: string;
  readonly sentAt: string | null;
  readonly ignoredCallbacks: number;
  readonly lastIgnored: CallbackKind | null;
}

const DELIVERY_KEYS = [
  "kind",
  "intent_id",
  "recommendation_id",
  "version",
  "channel",
  "severity",
  "state",
  "reason",
  "direction",
  "mode",
  "expiry",
  "created_at",
  "sent_at",
  "ignored_callbacks",
  "last_ignored",
] as const;

/** Decode a notification delivery row. */
export function decodeDeliveryRow(value: unknown, path = "delivery"): DeliveryRow {
  const found = exact(value, path, DELIVERY_KEYS);
  if (found.kind !== "delivery") {
    throw new ContractViolation(`${path}.kind`, "delivery");
  }
  const row: DeliveryRow = {
    kind: "delivery",
    intentId: field(found, path, "intent_id", str),
    recommendationId: field(found, path, "recommendation_id", str),
    version: field(found, path, "version", nat),
    channel: field(found, path, "channel", oneOf(CHANNELS)),
    severity: field(found, path, "severity", oneOf(SEVERITIES)),
    state: field(found, path, "state", oneOf(DELIVERY_STATES)),
    reason: field(found, path, "reason", nullable(oneOf(DELIVERY_REASONS))),
    direction: field(found, path, "direction", nullable(oneOf(DIRECTIONS))),
    mode: field(found, path, "mode", oneOf(MODES)),
    expiry: field(found, path, "expiry", instant),
    createdAt: field(found, path, "created_at", instant),
    sentAt: field(found, path, "sent_at", nullable(instant)),
    ignoredCallbacks: field(found, path, "ignored_callbacks", nat),
    lastIgnored: field(found, path, "last_ignored", nullable(oneOf(CALLBACK_KINDS))),
  };
  if (row.reason !== null && !REASON_STATES.includes(row.state)) {
    throw new ContractViolation(path, "a reason only for FAILED, EXPIRED or SUPERSEDED");
  }
  if ((row.lastIgnored !== null) !== row.ignoredCallbacks > 0) {
    throw new ContractViolation(path, "a last ignored kind exactly when callbacks were ignored");
  }
  return row;
}

export interface QuietHoursRow {
  readonly kind: "quiet_hours";
  readonly enabled: boolean;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly timezone: string;
  readonly updatedAt: string | null;
}

const QUIET_KEYS = [
  "kind",
  "enabled",
  "start_minute",
  "end_minute",
  "timezone",
  "silences",
  "not_silenceable",
  "updated_at",
] as const;

function exactly<const T extends readonly string[]>(allowed: T, what: string): Reader<T> {
  return (value, path) => {
    if (!Array.isArray(value) || value.length !== allowed.length) {
      throw new ContractViolation(path, what);
    }
    const list = value as unknown[];
    allowed.forEach((member, index) => {
      if (list[index] !== member) {
        throw new ContractViolation(path, what);
      }
    });
    return allowed;
  };
}

function minute(value: unknown, path: string): number {
  const found = nat(value, path);
  if (found >= MINUTES_PER_DAY) {
    throw new ContractViolation(path, `a minute of the day below ${String(MINUTES_PER_DAY)}`);
  }
  return found;
}

/**
 * Decode the quiet-hours row. A window that does not silence exactly NORMAL, or whose
 * not-silenceable list is not exactly SEV1 and MANDATORY, is refused (fail closed).
 */
export function decodeQuietHoursRow(value: unknown, path = "quiet_hours"): QuietHoursRow {
  const found = exact(value, path, QUIET_KEYS);
  if (found.kind !== "quiet_hours") {
    throw new ContractViolation(`${path}.kind`, "quiet_hours");
  }
  field(found, path, "silences", exactly(["NORMAL"] as const, "exactly NORMAL"));
  field(found, path, "not_silenceable", exactly(NOT_SILENCEABLE, "exactly SEV1 and MANDATORY"));
  return {
    kind: "quiet_hours",
    enabled: field(found, path, "enabled", bool),
    startMinute: field(found, path, "start_minute", minute),
    endMinute: field(found, path, "end_minute", minute),
    timezone: field(found, path, "timezone", str),
    updatedAt: field(found, path, "updated_at", nullable(instant)),
  };
}

export type NotificationsRow = DeliveryRow | QuietHoursRow;

/** Decode a `notifications` projection row by its `kind`. */
export function decodeNotificationsRow(value: unknown, path = "notifications"): NotificationsRow {
  const kind = (value as { kind?: unknown } | null)?.kind;
  if (kind === "delivery") {
    return decodeDeliveryRow(value, path);
  }
  if (kind === "quiet_hours") {
    return decodeQuietHoursRow(value, path);
  }
  throw new ContractViolation(path, "a delivery or quiet_hours row");
}

export interface ExportRequest {
  readonly clientKey: string;
  readonly kind: JobKind;
  readonly workspaceId: string;
  readonly accountId: string | null;
  readonly sharedWorkspace: string | null;
  readonly mode: Mode;
  readonly fidelity: Fidelity;
  readonly query: readonly (readonly [string, string])[];
  readonly ttlSeconds: number | null;
}

/** Encode a create request as the keys `ssi.api.routes_jobs` reads (absent scope ids omitted). */
export function encodeExportRequest(request: ExportRequest): Record<string, unknown> {
  const scope: Record<string, string> = { workspace_id: request.workspaceId };
  if (request.accountId !== null) {
    scope.account_id = request.accountId;
  }
  if (request.sharedWorkspace !== null) {
    scope.shared_workspace = request.sharedWorkspace;
  }
  return {
    client_key: request.clientKey,
    kind: request.kind,
    scope,
    mode: request.mode,
    fidelity: request.fidelity,
    query: Object.fromEntries(request.query),
    ...(request.ttlSeconds === null ? {} : { ttl_seconds: request.ttlSeconds }),
  };
}

export interface QuietHoursRequest {
  readonly clientKey: string;
  readonly enabled: boolean;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly reason: string;
}

/** Encode the quiet-hours command body. */
export function encodeQuietHoursRequest(request: QuietHoursRequest): Record<string, unknown> {
  return {
    client_key: request.clientKey,
    enabled: request.enabled,
    start_minute: request.startMinute,
    end_minute: request.endMinute,
    reason: request.reason,
  };
}
